import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { type SharedValue } from 'react-native-reanimated';

import { CustomNavigationHeader, theme } from '@sg/garnish';

import { Bag } from '@order/features-ordering';

import { type useOrderState } from '../../hooks';

/**
 * Header for the animated dialog.
 */
export const BagAnimatedDialogHeader = (props: BagAnimatedDialogHeaderProps) => {
  const { show, orderState, scrollOffsetSV, closeA11yLabel } = props;
  const {
    isChangingTime,
    isChangingDeliveryPreferences,
    isShowingCheckoutLedger,
    isChangingPaymentMethod,
    isShowingGiftCardForm,
    isShowingLoyaltyInfo,
    cancelRequest,
    goBack,
  } = orderState;

  const { formatMessage } = useIntl();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleClosePress = useCallback(() => {
    cancelRequest();
  }, [cancelRequest]);

  // ─── Styles ───────────────────────────────────────────────────────────────

  const headerStyles = [styles.header, !show && styles.hiddenHeader];

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <CustomNavigationHeader.Container
      palette="cream"
      safeAreaEdges={[]}
      style={headerStyles}
      scrollOffsetSV={show ? scrollOffsetSV : undefined}
    >
      {goBack ? (
        <CustomNavigationHeader.Button.Left
          name="IconCaretLeft"
          palette="dark-kale"
          accessibilityLabel={formatMessage(messages.back)}
          onPress={goBack}
        />
      ) : null}

      <CustomNavigationHeader.Content
        pointerEvents="none"
        scrollOffsetSV={show ? scrollOffsetSV : undefined}
      >
        <Bag.AnimatedDialogTitle
          isShowingCheckoutLedger={isShowingCheckoutLedger}
          isChangingTime={isChangingTime}
          isChangingDeliveryPreferences={isChangingDeliveryPreferences}
          isChangingPaymentMethod={isChangingPaymentMethod}
          isShowingGiftCardForm={isShowingGiftCardForm}
          isShowingLoyaltyInfo={isShowingLoyaltyInfo}
        />
      </CustomNavigationHeader.Content>

      <CustomNavigationHeader.Button.Right
        name="IconClose"
        palette="dark-kale"
        accessibilityLabel={closeA11yLabel ?? formatMessage(messages.close)}
        onPress={handleClosePress}
      />
    </CustomNavigationHeader.Container>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  back: {
    defaultMessage: 'Go back',
    description: 'Bag | Header | Go back',
  },
  close: {
    defaultMessage: 'Close bag dialog',
    description: 'Bag | Header | Close',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    marginBottom: -theme.spacing['18'],
  },
  hiddenHeader: {
    maxHeight: 0,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagAnimatedDialogHeaderProps = {
  orderState: ReturnType<typeof useOrderState>;
  scrollOffsetSV: SharedValue<number>;
  show?: boolean;
  closeA11yLabel?: string;
};
